import type { Temporal } from '@js-temporal/polyfill'
import type { AspectRatio } from '@lucimark/shared/database/custom-types/aspect-ratio'
import type {
  IChannelPayloadPlaylistAssignment,
  IPlaybackScheduleRoot
} from '../models/player-registration-payload'
import type { TGeoLatLng } from '../models/playlist-assignment-geo-filter'
import type { TFrame } from '../models/playlist'
import type { CampaignFrameSequenceRef } from './buildFrameSequenceRefs'
import type { TPortalBookFrameSequencePlaylist } from './buildPortalBookFrameSequenceRefs'
import { coercePlaybackMode, PlaybackMode } from '@lucimark/shared/database/custom-types/playback-mode'
import { frameLayoutAspectBucket } from './aspectRatioFromPlaylistFrameLayouts'
import { buildFrameSequenceRefs } from './buildFrameSequenceRefs'
import {
  buildPortalBookInterleavedFrameSequenceRefs,
  isPortalBookHouseEmbedSlot,
  isPortalBookPaidOrderPlaylist
} from './buildPortalBookFrameSequenceRefs'
import { normalizePlaylistRef } from './normalizePlaylistPayload'
import {
  expandNestedPlaylistRefFrame,
  hasExpandableNestedPlaylistRefFrame
} from './portalCpsHouseEligibleFrames'
import { isFrameActiveAt, isPlaylistAssignmentActiveForPlayback, type TFrameActiveAtOptions } from './selectActiveCampaign'
import { commitPortalBookHouseRotatingAdvances, countEmbeddedPlaylistRotatingAnchors, selectEmbeddedFrames } from './selectEmbeddedFrames'
import { frameHasVideoFacet } from './frameHasVideoFacet'

function scheduleRootUrnForMasterBypass(scheduleRoot: IPlaybackScheduleRoot): string | undefined {
  const urn = (scheduleRoot as { scheduleRootUrn?: string }).scheduleRootUrn
  return typeof urn === 'string' ? urn : undefined
}

function frameActiveAtOptions(options?: ResolvePlaybackSegmentsOptions): TFrameActiveAtOptions | undefined {
  if (options == null) {
    return undefined
  }
  if (options.skipGeoFilter === true) {
    return { skipGeoFilter: true }
  }
  if (options.geoPoint !== undefined) {
    return { geoPoint: options.geoPoint }
  }
  return undefined
}

/** Portal book paid orders always match player viewport when set; otherwise fall back to editorial filter. */
function portalBookAspectAllowedSet(
  options?: ResolvePlaybackSegmentsOptions
): ReadonlySet<AspectRatio> | null {
  const viewport = options?.portalBookViewportAspect
  if (viewport != null) {
    return new Set([viewport])
  }
  return options?.allowedAspectRatios ?? null
}

function isFrameAllowedByAspect(
  frame: TFrame,
  allowed: ReadonlySet<AspectRatio> | null | undefined
): boolean {
  /** RSS directory frames are responsive full-viewport content; skip player layout aspect filtering. */
  if (frame.rssDirectoryRef != null) {
    return true
  }
  if (allowed == null || allowed.size === 0) {
    return true
  }
  return allowed.has(frameLayoutAspectBucket(frame))
}

/** Aspect + optional low-capacity video exclusion (skip whole frames with any video facet). */
function isFramePlaybackEligible(
  frame: TFrame,
  allowed: ReadonlySet<AspectRatio> | null | undefined,
  excludeFramesWithVideo?: boolean
): boolean {
  if (!isFrameAllowedByAspect(frame, allowed)) {
    return false
  }
  if (excludeFramesWithVideo === true && frameHasVideoFacet(frame)) {
    return false
  }
  return true
}

/** Resolves `embeddedPlaylistFrames[refUrn]` with case-insensitive key fallback (URN casing can differ across layers). */
function embeddedFramesForPlaylistRef(
  embedded: Readonly<Record<string, TFrame[]>> | undefined,
  refUrn: string
): TFrame[] {
  if (embedded == null || refUrn === '') {
    return []
  }
  const direct = embedded[refUrn]
  if (direct != null) {
    return direct
  }
  const want = refUrn.trim().toLowerCase()
  if (want === '') {
    return []
  }
  for (const [k, frames] of Object.entries(embedded)) {
    if (k.trim().toLowerCase() === want) {
      return frames
    }
  }
  return []
}

function portalBookCpsFrameId(id: string): boolean {
  return id.startsWith('book-')
}

function mainFacetAssetUrn(frame: TFrame): string {
  const asset = frame.facets?.main?.asset
  return typeof asset === 'string' ? asset.trim() : ''
}

/**
 * Book CPS playlists may still contain legacy slack rows that reuse house-pool video (looks like HF3 twice).
 * Drop those rows; keep creatives whose assets are not in the embedded house pool.
 */
function stripPortalBookHousePadCpsSegments(
  segments: readonly ResolvedPlaybackSegment[],
  scheduleRoot: IPlaybackScheduleRoot,
  activePlaylists: readonly TPortalBookFrameSequencePlaylist[]
): ResolvedPlaybackSegment[] {
  const housePlaylist = activePlaylists.find(p => isPortalBookHouseEmbedSlot(p.frames ?? []))
  const houseEmbedUrn = housePlaylist?.frames?.[0]?.playlistRef?.playlistUrn?.trim() ?? ''
  if (houseEmbedUrn === '') {
    return [...segments]
  }
  const houseAssets = new Set(
    embeddedFramesForPlaylistRef(scheduleRoot.embeddedPlaylistFrames, houseEmbedUrn)
      .map(f => mainFacetAssetUrn(f))
      .filter(u => u !== '')
  )
  if (houseAssets.size === 0) {
    return [...segments]
  }
  const out: ResolvedPlaybackSegment[] = []
  for (const seg of segments) {
    const asset = mainFacetAssetUrn(seg.frame)
    if (portalBookCpsFrameId(seg.frame.id) && houseAssets.has(asset)) {
      continue
    }
    out.push(seg)
  }
  return out
}

/** Re-chain remaining book CPS rows in each paid-order block after house-pad rows are removed. */
function normalizePortalBookPaidOrderContinuousLinks(segments: ResolvedPlaybackSegment[]): void {
  let i = 0
  while (i < segments.length) {
    if (!portalBookCpsFrameId(segments[i]!.frame.id)) {
      i++
      continue
    }
    const urn = segments[i]!.playlistUrn
    let j = i
    while (
      j + 1 < segments.length
      && portalBookCpsFrameId(segments[j + 1]!.frame.id)
      && segments[j + 1]!.playlistUrn === urn
    ) {
      j++
    }
    for (let k = i; k <= j; k++) {
      const frame = segments[k]!.frame
      if (k < j) {
        segments[k] = { ...segments[k]!, frame: { ...frame, playContinuousWithNext: true as const } }
      }
      else if (frame.playContinuousWithNext === true) {
        const { playContinuousWithNext: _omit, ...rest } = frame
        segments[k] = { ...segments[k]!, frame: rest as TFrame }
      }
    }
    i = j + 1
  }
}

function embedFrameForResolvedSegment(
  frame: TFrame,
  indexInSelection: number,
  selectionLength: number
): TFrame {
  if (selectionLength <= 1) {
    return frame
  }
  // Bond head keeps playContinuousWithNext so the player can seamless-handoff into followers.
  if (indexInSelection === 0 && frame.playContinuousWithNext === true) {
    return frame
  }
  if (frame.playContinuousWithNext === true) {
    const { playContinuousWithNext: _omit, ...rest } = frame
    return rest as TFrame
  }
  return frame
}

/**
 * Portal book house picks must match paid order creative aspect (e.g. portrait book + portrait HF1–3),
 * not other assets in the same embedded pool (landscape promos) when the viewport filter is off or in flux.
 */
function portalBookPaidOrderAspectBucket(
  activePlaylists: readonly TPortalBookFrameSequencePlaylist[],
  referenceInstant: Temporal.Instant,
  allowed: ReadonlySet<AspectRatio> | null | undefined,
  excludeFramesWithVideo?: boolean,
  options?: ResolvePlaybackSegmentsOptions
): AspectRatio | null {
  for (const pl of activePlaylists) {
    if (!isPortalBookPaidOrderPlaylist(pl)) {
      continue
    }
    for (const frame of pl.frames ?? []) {
      if (!isFrameActiveAt(frame, referenceInstant, frameActiveAtOptions(options)) || !isFramePlaybackEligible(frame, allowed, excludeFramesWithVideo)) {
        continue
      }
      return frameLayoutAspectBucket(frame)
    }
  }
  return null
}

function portalBookHouseEmbedPoolFrames(
  scheduleRoot: IPlaybackScheduleRoot,
  activePlaylists: readonly TPortalBookFrameSequencePlaylist[],
  referenceInstant: Temporal.Instant,
  options?: ResolvePlaybackSegmentsOptions
): TFrame[] {
  const housePlaylist = activePlaylists.find(p => isPortalBookHouseEmbedSlot(p.frames ?? []))
  const houseEmbedUrn = housePlaylist?.frames?.[0]?.playlistRef?.playlistUrn?.trim() ?? ''
  if (houseEmbedUrn === '') {
    return []
  }
  const allowed = portalBookAspectAllowedSet(options)
  const excludeVideo = options?.excludeFramesWithVideo
  const bookAspect = portalBookPaidOrderAspectBucket(
    activePlaylists,
    referenceInstant,
    allowed,
    excludeVideo,
    options
  )
  const pool = embeddedFramesForPlaylistRef(scheduleRoot.embeddedPlaylistFrames, houseEmbedUrn)
    .filter(
      ef => !ef.playlistRef
        && isFrameActiveAt(ef, referenceInstant, frameActiveAtOptions(options))
        && isFramePlaybackEligible(ef, allowed, excludeVideo)
    )
  if (bookAspect == null) {
    return pool
  }
  return pool.filter(ef => ef.rssDirectoryRef != null || frameLayoutAspectBucket(ef) === bookAspect)
}

function portalBookInterleavedFrameSequenceParams(
  scheduleRoot: IPlaybackScheduleRoot,
  activePlaylists: readonly TPortalBookFrameSequencePlaylist[],
  referenceInstant: Temporal.Instant,
  options?: ResolvePlaybackSegmentsOptions
): Pick<
  Parameters<typeof buildPortalBookInterleavedFrameSequenceRefs>[0],
  'houseEmbedPoolFrameCount' | 'houseEmbedPoolAnchorCount'
> {
  const poolFrames = portalBookHouseEmbedPoolFrames(
    scheduleRoot,
    activePlaylists,
    referenceInstant,
    options
  )
  return {
    houseEmbedPoolFrameCount: poolFrames.length,
    houseEmbedPoolAnchorCount: countEmbeddedPlaylistRotatingAnchors(poolFrames)
  }
}

/**
 * One step in the ordered playback loop for a schedule root at a reference instant.
 * `playlistSeqNo` matches the index in the full frame-sequence refs (same as player `playlistSeqNo` when no refs are skipped).
 */
export interface ResolvedPlaybackSegment {
  playlistUrn: string
  /**
   * Playlist URN that owns the segment’s assets for proof-of-play / billing.
   * Matches {@link playlistUrn} for normal frames; for expanded embeds it is the **embedded** playlist ref URN
   * (while `playlistUrn` stays the parent row’s playlist for scheduler/worker identity).
   */
  contentSourcePlaylistUrn: string
  /** Index in the playlist’s `frames` array for this segment. */
  frameIndex: number
  playlistSeqNo: number
  frame: TFrame
  playlistWidgetConfig?: IChannelPayloadPlaylistAssignment['widgetConfig']
  metadata?: IChannelPayloadPlaylistAssignment['metadata']
}

/**
 * JSON-safe, worker-friendly slice of {@link ResolvedPlaybackSegment} for scheduler sync (no full `TFrame` / facets).
 */
export interface PlaybackSegment {
  playlistUrn: string
  frameIndex: number
  playlistSeqNo: number
  durationSeconds: number
  frameId: string
  /** True when this frame requests a minimal-gap handoff into the next segment (same playlist order). */
  playContinuousWithNext?: boolean
}

/**
 * Optional resolver behaviour (player snapshot layer).
 * {@link embeddedRandomPickCache} keeps `selectionMode: random` draws stable across periodic
 * `rebuildQueue()` calls so `selectEmbeddedFrames` is not re-entered every second. Keys include
 * spine index so the same embed frame can draw independently at each visit in one loop.
 * {@link scheduleRootRandomRefOrderCache} memoizes schedule-root {@link PlaybackMode.Random} frame order per spine.
 */
export interface ResolvePlaybackSegmentsOptions {
  /**
   * When set, `random` embedded picks are memoized per parent slot + active pool + ref config.
   * Cleared when the player’s schedule snapshot is reconfigured (new assignment).
   */
  embeddedRandomPickCache?: Map<string, TFrame[]>
  /**
   * When set, schedule-root `playbackMode: random` uses a stable shuffled ref order keyed by
   * {@link scheduleFingerprint} (rotating spine + root mode), cleared with embedded random cache on loop wrap.
   */
  scheduleRootRandomRefOrderCache?: Map<string, CampaignFrameSequenceRef[]>
  /**
   * When non-null and non-empty, only frames (including embedded playlist frames) whose
   * `layout.aspectRatio` is in this set are included in segments and {@link scheduleFingerprint}.
   */
  allowedAspectRatios?: ReadonlySet<AspectRatio> | null
  /**
   * When true, omit frames (including embedded) that have any {@link AssetType.Video} facet.
   * Used for low-capacity devices when tenant disallows video.
   */
  excludeFramesWithVideo?: boolean
  /**
   * Registered player viewport aspect for Lucimark Book paid orders. When set, portal book
   * creatives and house embed picks use this even if {@link allowedAspectRatios} is null.
   */
  portalBookViewportAspect?: AspectRatio | null
  /** Operator-assigned master playlist URN; that row stays active outside its `timeBlocks`. */
  operatorMasterPlaylistUrn?: string | null
  /**
   * Resolved player geo point (GPS → venue → stamped fallback) for assignment and frame geofilters.
   * When a row or frame has {@link geoFilter} and this is `null`, geo-gated items are inactive
   * unless the frame sets {@link showWhenLocationUnavailable}. Omit for time-only evaluation.
   */
  geoPoint?: TGeoLatLng | null
  /**
   * When true, ignore playlist assignment and frame {@link geoFilter} (idle diagnostics /
   * combine spine). Live playback must leave this unset/false.
   */
  skipGeoFilter?: boolean
  /**
   * When true, advance portal-book house rotating cursor after this resolve (loop wrap only).
   * Periodic materialization must leave this false so one loop keeps stable HF1/HF2 picks.
   */
  commitPortalBookHouseRotatingAdvance?: boolean
  /**
   * When true, advance house rotating embed cursor after this resolve (loop wrap only).
   * Periodic materialization must leave this false so the on-air pick does not jump early.
   */
  commitRotatingEmbedAdvance?: boolean
}

/**
 * Maps full segments to a minimal payload suitable for `postMessage` / Comlink to a playback scheduler worker.
 */
export function playbackSegmentsToWorkerPayload(
  segments: ResolvedPlaybackSegment[]
): PlaybackSegment[] {
  return segments.map(s => ({
    playlistUrn: s.playlistUrn,
    frameIndex: s.frameIndex,
    playlistSeqNo: s.playlistSeqNo,
    durationSeconds: s.frame.duration,
    frameId: s.frame.id,
    playContinuousWithNext: s.frame.playContinuousWithNext === true ? true : undefined
  }))
}

/**
 * Stable string for sequence hashing. Matches the player’s queue sequence serializer (`id` + `duration` per frame)
 * when each worker row was derived from the same ordered queue.
 */
export function serializePlaybackSegmentsForSequenceHash(
  segments: PlaybackSegment[]
): string {
  return segments.map(r => `${r.frameId} ${r.durationSeconds}`).join(' ')
}

/**
 * Stable spine of “what schedule is active” at `referenceInstant`: parent playlist rows and embed **slots**
 * (playlistRef identity + mode), **without** expanding embedded frames or calling {@link selectEmbeddedFrames}.
 *
 * Used by the player snapshot layer so `peekWorkerSequenceHashAt` can return the **materialized** worker
 * sequence hash when only embedded picks would differ — otherwise every peek looked one rotating step ahead
 * of the current queue and forced a rebuild every tick (scheduler reset → stuck on first spine frame).
 */
export function scheduleFingerprint(
  scheduleRoot: IPlaybackScheduleRoot | null,
  referenceInstant: Temporal.Instant,
  options?: ResolvePlaybackSegmentsOptions
): string {
  if (scheduleRoot === null || (scheduleRoot.playlists?.length ?? 0) === 0) {
    return ''
  }

  const allowed = options?.allowedAspectRatios
  const excludeVideo = options?.excludeFramesWithVideo

  const activePlaylists = scheduleRoot.playlists.filter(ap =>
    isPlaylistAssignmentActiveForPlayback(ap, referenceInstant, options?.operatorMasterPlaylistUrn, {
      scheduleRootUrn: scheduleRootUrnForMasterBypass(scheduleRoot),
      geoPoint: options?.geoPoint,
      skipGeoFilter: options?.skipGeoFilter
    })
  )
  if (activePlaylists.length === 0) {
    return ''
  }

  const assignments = activePlaylists.map(ap => ({
    playlistUrn: ap.playlistUrn,
    frameCount: (ap.frames ?? []).length,
    playWithoutInterruption: ap.playWithoutInterruption ?? false
  }))

  const portalBookRefs = buildPortalBookInterleavedFrameSequenceRefs({
    assignments,
    playlists: activePlaylists,
    ...portalBookInterleavedFrameSequenceParams(
      scheduleRoot,
      activePlaylists,
      referenceInstant,
      options
    )
  })
  const refs = portalBookRefs ?? buildFrameSequenceRefs(PlaybackMode.Rotating, assignments)

  const playlistsByUrn = new Map(activePlaylists.map(ap => [ap.playlistUrn, ap]))
  const parts: string[] = [`rootPlaybackMode\t${coercePlaybackMode(scheduleRoot.playbackMode)}`]

  for (let i = 0; i < refs.length; i++) {
    const { urn, frameIndex } = refs[i]!
    const playlist = playlistsByUrn.get(urn)
    const listFrames = playlist?.frames ?? []
    const frame = listFrames[frameIndex] ?? null
    if (frame == null || playlist == null) {
      continue
    }
    if (!isFrameActiveAt(frame, referenceInstant, frameActiveAtOptions(options))) {
      continue
    }

    if (frame.playlistRef) {
      const embeddedFrames = embeddedFramesForPlaylistRef(
        scheduleRoot.embeddedPlaylistFrames,
        frame.playlistRef.playlistUrn
      )
      const activeEmbedded = embeddedFrames.filter(
        ef => !ef.playlistRef && isFrameActiveAt(ef, referenceInstant, frameActiveAtOptions(options)) && isFramePlaybackEligible(ef, allowed, excludeVideo)
      )
      if (activeEmbedded.length === 0) {
        continue
      }
      const normalizedRef = normalizePlaylistRef(frame.playlistRef) ?? frame.playlistRef
      const rawMode = normalizedRef.selectionMode as 'random' | 'rotating' | 'sequential'
      const selectionMode = rawMode === 'sequential' ? 'rotating' : rawMode
      parts.push(
        `embed\t${i}\t${urn}\t${frameIndex}\t${frame.id}\t${normalizedRef.playlistUrn}\t${String(normalizedRef.frameCount ?? '')}\t${selectionMode}`
      )
      continue
    }

    if (!isFramePlaybackEligible(frame, allowed, excludeVideo)) {
      continue
    }

    parts.push(`frame\t${i}\t${urn}\t${frameIndex}\t${frame.id}`)
  }

  if (allowed != null && allowed.size > 0) {
    parts.push(`allowedAspectRatios\t${[...allowed].sort().join(',')}`)
  }
  if (excludeVideo === true) {
    parts.push('excludeFramesWithVideo\t1')
  }
  const portalBookViewport = options?.portalBookViewportAspect
  if (portalBookViewport != null) {
    parts.push(`portalBookViewportAspect\t${portalBookViewport}`)
  }

  return parts.join('\n')
}

function resolveFrameRefsForScheduleRootPlaylists(
  scheduleRoot: IPlaybackScheduleRoot,
  activePlaylists: readonly TPortalBookFrameSequencePlaylist[],
  assignments: { playlistUrn: string, frameCount: number, playWithoutInterruption: boolean }[],
  referenceInstant: Temporal.Instant,
  options?: ResolvePlaybackSegmentsOptions
): CampaignFrameSequenceRef[] {
  const portalBookRefs = buildPortalBookInterleavedFrameSequenceRefs({
    assignments,
    playlists: activePlaylists,
    ...portalBookInterleavedFrameSequenceParams(
      scheduleRoot,
      activePlaylists,
      referenceInstant,
      options
    )
  })
  if (portalBookRefs != null) {
    return portalBookRefs
  }
  const rotatingRefs = buildFrameSequenceRefs(PlaybackMode.Rotating, assignments)
  const mode = coercePlaybackMode(scheduleRoot.playbackMode)
  if (mode !== PlaybackMode.Random) {
    return rotatingRefs
  }
  const cache = options?.scheduleRootRandomRefOrderCache
  const spineKey = scheduleFingerprint(scheduleRoot, referenceInstant, options)
  const cacheKey = `rootRandomOrder\t${spineKey}`
  if (cache != null) {
    const hit = cache.get(cacheKey)
    if (hit != null) {
      return hit
    }
    const shuffled = buildFrameSequenceRefs(PlaybackMode.Random, assignments)
    cache.set(cacheKey, shuffled)
    return shuffled
  }
  return buildFrameSequenceRefs(PlaybackMode.Random, assignments)
}

/**
 * Pure resolver: given a schedule root and UTC instant, returns ordered segments
 * (active playlist rows only — startDate/endDate/timeBlocks). Ordering matches
 * {@link buildFrameSequenceRefs} for the effective {@link PlaybackMode}.
 *
 * Used by the player for queue build (#277) and intended as the single source of truth shared with a future scheduler worker.
 */
export function resolvePlaybackSegments(
  scheduleRoot: IPlaybackScheduleRoot | null,
  referenceInstant: Temporal.Instant,
  options?: ResolvePlaybackSegmentsOptions
): ResolvedPlaybackSegment[] {
  if (scheduleRoot === null || (scheduleRoot.playlists?.length ?? 0) === 0) {
    return []
  }

  const activePlaylists = scheduleRoot.playlists.filter(ap =>
    isPlaylistAssignmentActiveForPlayback(ap, referenceInstant, options?.operatorMasterPlaylistUrn, {
      scheduleRootUrn: scheduleRootUrnForMasterBypass(scheduleRoot),
      geoPoint: options?.geoPoint,
      skipGeoFilter: options?.skipGeoFilter
    })
  )
  if (activePlaylists.length === 0) {
    return []
  }

  const assignments = activePlaylists.map(ap => ({
    playlistUrn: ap.playlistUrn,
    frameCount: (ap.frames ?? []).length,
    playWithoutInterruption: ap.playWithoutInterruption ?? false
  }))

  const portalBookParams = portalBookInterleavedFrameSequenceParams(
    scheduleRoot,
    activePlaylists,
    referenceInstant,
    options
  )
  const portalBookRefs = buildPortalBookInterleavedFrameSequenceRefs({
    assignments,
    playlists: activePlaylists,
    ...portalBookParams
  })
  const refs = resolveFrameRefsForScheduleRootPlaylists(
    scheduleRoot,
    activePlaylists,
    assignments,
    referenceInstant,
    options
  )
  const usePortalBook = portalBookRefs != null

  const playlistsByUrn = new Map(activePlaylists.map(ap => [ap.playlistUrn, ap]))
  const segments: ResolvedPlaybackSegment[] = []
  const pickCache = options?.embeddedRandomPickCache
  let portalBookHouseVisitIndex = 0
  let portalBookHouseEmbedFrameId = ''
  let portalBookHouseAnchorCount = 0
  let portalBookHouseVisitCount = 0

  for (let i = 0; i < refs.length; i++) {
    const { urn, frameIndex } = refs[i]!
    const playlist = playlistsByUrn.get(urn)
    const listFrames = playlist?.frames ?? []
    const frame = listFrames[frameIndex] ?? null
    if (frame == null || playlist == null) {
      continue
    }
    if (!isFrameActiveAt(frame, referenceInstant, frameActiveAtOptions(options))) {
      continue
    }

    // Expand embedded playlist frame into its referenced frames.
    if (frame.playlistRef) {
      const embeddedFrames = embeddedFramesForPlaylistRef(
        scheduleRoot.embeddedPlaylistFrames,
        frame.playlistRef.playlistUrn
      )
      // Depth-2 `playlistRef` inside embedded content stays excluded; one nested level is expanded.
      const embeddedMap = scheduleRoot.embeddedPlaylistFrames ?? {}
      const activeEmbedded = embeddedFrames.filter(
        ef => isFrameActiveAt(ef, referenceInstant, frameActiveAtOptions(options))
          && isFramePlaybackEligible(ef, options?.allowedAspectRatios, options?.excludeFramesWithVideo)
          && (ef.playlistRef == null || hasExpandableNestedPlaylistRefFrame(ef, embeddedMap))
      )
      const normalizedRef = normalizePlaylistRef(frame.playlistRef) ?? frame.playlistRef
      const rawMode = normalizedRef.selectionMode as 'random' | 'rotating' | 'sequential'
      const selectionMode = rawMode === 'sequential' ? 'rotating' : rawMode

      const poolSig = activeEmbedded.map(f => f.id).sort().join('\0')
      const refSig = `${normalizedRef.playlistUrn}\0${String(normalizedRef.frameCount)}\0${selectionMode}\0${String(normalizedRef.offset ?? 0)}`
      // Include spine index so portal book (and similar) can visit the same embed slot more than once per loop.
      const cacheKey = `${urn}\0${String(frameIndex)}\0${frame.id}\0${String(i)}\0${poolSig}\0${refSig}`

      let selected: TFrame[]
      if (
        pickCache != null
        && selectionMode === 'random'
        && activeEmbedded.length > 0
        && pickCache.has(cacheKey)
      ) {
        selected = pickCache.get(cacheKey)!
      }
      else {
        const isPortalBookHouseVisit = usePortalBook
          && isPortalBookHouseEmbedSlot(listFrames)
          && selectionMode === 'rotating'
        // Rotating shares one cursor per embed row; random uses spine index so each visit can draw independently.
        const embedStateKey = selectionMode === 'rotating'
          ? frame.id
          : `${frame.id}\0${String(i)}`
        if (isPortalBookHouseVisit) {
          portalBookHouseEmbedFrameId = frame.id
          const bookAspect = portalBookPaidOrderAspectBucket(
            activePlaylists,
            referenceInstant,
            portalBookAspectAllowedSet(options),
            options?.excludeFramesWithVideo,
            options
          )
          const housePool = bookAspect == null
            ? activeEmbedded
            : activeEmbedded.filter(ef => ef.rssDirectoryRef != null || frameLayoutAspectBucket(ef) === bookAspect)
          portalBookHouseAnchorCount = countEmbeddedPlaylistRotatingAnchors(housePool)
          const visitIdx = portalBookHouseVisitIndex
          selected = selectEmbeddedFrames(
            housePool.length > 0 ? housePool : activeEmbedded,
            normalizedRef,
            frame.id,
            {
              portalBookHouseVisitIndex: portalBookHouseVisitIndex++
            }
          )
          portalBookHouseVisitCount++
        }
        else {
          selected = selectEmbeddedFrames(activeEmbedded, normalizedRef, embedStateKey, {
            commitRotatingEmbedAdvance: options?.commitRotatingEmbedAdvance === true
          })
        }
        if (pickCache != null && selectionMode === 'random' && selected.length > 0) {
          pickCache.set(cacheKey, selected)
        }
      }
      for (let j = 0; j < selected.length; j++) {
        const item = selected[j]!
        const resolvedFrames = item.playlistRef
          ? expandNestedPlaylistRefFrame(
              item,
              embeddedMap,
              inner => isFrameActiveAt(inner, referenceInstant, frameActiveAtOptions(options))
                && isFramePlaybackEligible(inner, options?.allowedAspectRatios, options?.excludeFramesWithVideo)
            )
          : [item]
        const nestedSourceUrn = item.playlistRef
          ? (normalizePlaylistRef(item.playlistRef)?.playlistUrn ?? normalizedRef.playlistUrn)
          : normalizedRef.playlistUrn
        for (let k = 0; k < resolvedFrames.length; k++) {
          const ef = embedFrameForResolvedSegment(resolvedFrames[k]!, k, resolvedFrames.length)
          segments.push({
            playlistUrn: urn,
            contentSourcePlaylistUrn: item.playlistRef ? nestedSourceUrn : normalizedRef.playlistUrn,
            frameIndex,
            playlistSeqNo: i,
            frame: ef,
            playlistWidgetConfig: playlist.widgetConfig,
            metadata: playlist.metadata
          })
        }
      }
      continue
    }

    if (!isFramePlaybackEligible(frame, options?.allowedAspectRatios, options?.excludeFramesWithVideo)) {
      continue
    }

    if (
      usePortalBook
      && isPortalBookPaidOrderPlaylist(playlist)
      && frameIndex > 0
    ) {
      continue
    }

    if (usePortalBook && isPortalBookPaidOrderPlaylist(playlist)) {
      const assignmentCap = assignments.find(a => a.playlistUrn === urn)?.frameCount
      const cap = assignmentCap ?? listFrames.length
      const paidFrames = listFrames
        .slice(0, Math.max(0, Math.min(cap, listFrames.length)))
        .filter(
          f =>
            isFrameActiveAt(f, referenceInstant, frameActiveAtOptions(options))
            && isFramePlaybackEligible(f, portalBookAspectAllowedSet(options), options?.excludeFramesWithVideo)
        )
      for (let j = 0; j < paidFrames.length; j++) {
        const raw = paidFrames[j]!
        const linked = j < paidFrames.length - 1
          ? { ...raw, playContinuousWithNext: true as const }
          : raw
        segments.push({
          playlistUrn: urn,
          contentSourcePlaylistUrn: urn,
          frameIndex: j,
          playlistSeqNo: i,
          frame: embedFrameForResolvedSegment(linked, j, paidFrames.length),
          playlistWidgetConfig: playlist.widgetConfig,
          metadata: playlist.metadata
        })
      }
      continue
    }

    segments.push({
      playlistUrn: urn,
      contentSourcePlaylistUrn: urn,
      frameIndex,
      playlistSeqNo: i,
      frame,
      playlistWidgetConfig: playlist.widgetConfig,
      metadata: playlist.metadata
    })
  }

  if (
    usePortalBook
    && portalBookHouseVisitCount > 0
    && portalBookHouseAnchorCount > 0
    && options?.commitPortalBookHouseRotatingAdvance === true
  ) {
    commitPortalBookHouseRotatingAdvances(
      portalBookHouseEmbedFrameId,
      portalBookHouseVisitCount,
      portalBookHouseAnchorCount
    )
  }

  if (usePortalBook && segments.length > 0) {
    const stripped = stripPortalBookHousePadCpsSegments(segments, scheduleRoot, activePlaylists)
    normalizePortalBookPaidOrderContinuousLinks(stripped)
    return stripped
  }

  return segments
}
